/**
 * PortalFlowBuilder — Visual flow editor
 *
 * Simple node-based editor for creating deterministic flows.
 * Users can add question, decision, collect, route, and end nodes,
 * configure rules, and connect them in a directed graph.
 *
 * All nodes are persisted to flow_nodes + flow_definitions tables.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Target, Plus, Trash2, Save, Eye, Code, ArrowLeft,
  MessageSquare, GitBranch, Zap, Check, X, Copy, Play,
  Loader2,
} from 'lucide-react';
import type { FlowNode, NodeType, FlowRule, RuleOperator } from '@/engine/types';
import PortalLayout from '@/components/portal/PortalLayout';
import { FlowChatWidget } from '@/components/chat/FlowChatWidget';

interface ChatbotRow {
  id: string;
  name: string;
  description: string | null;
  published?: boolean;
}

interface FlowNodeRow extends FlowNode {
  chatbot_id: string;
}

const NODE_TYPES: { value: NodeType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'question', label: 'Question', icon: <MessageSquare className="h-4 w-4" />, color: 'emerald' },
  { value: 'decision', label: 'Decision', icon: <GitBranch className="h-4 w-4" />, color: 'blue' },
  { value: 'collect', label: 'Collect', icon: <Target className="h-4 w-4" />, color: 'violet' },
  { value: 'route', label: 'Route', icon: <Zap className="h-4 w-4" />, color: 'amber' },
  { value: 'end', label: 'End', icon: <Check className="h-4 w-4" />, color: 'rose' },
];

const OPERATORS: { value: RuleOperator; label: string }[] = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
  { value: 'gte', label: 'greater or equal' },
  { value: 'lte', label: 'less or equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'exists', label: 'exists' },
  { value: 'not_exists', label: 'not exists' },
  { value: 'in', label: 'is one of' },
];

export default function PortalFlowBuilder() {
  const { id: chatbotId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [chatbot, setChatbot] = useState<ChatbotRow | null>(null);
  const [nodes, setNodes] = useState<FlowNodeRow[]>([]);
  const [selectedNode, setSelectedNode] = useState<FlowNodeRow | null>(null);
  const [flowDefId, setFlowDefId] = useState<string | null>(null);
  const [startNodeId, setStartNodeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);

  // Load chatbot + nodes
  useEffect(() => {
    if (!chatbotId) return;
    loadData();
  }, [chatbotId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [cbRows, flowNodes, flowDefs] = await Promise.all([
        supabaseGet<ChatbotRow[]>(`chatbots?select=id,name,description,published&id=eq.${chatbotId}`),
        supabaseGet<FlowNodeRow[]>(`flow_nodes?select=*&chatbot_id=eq.${chatbotId}&order=position`),
        supabaseGet<any[]>(`flow_definitions?select=*&chatbot_id=eq.${chatbotId}`),
      ]);

      const cb = cbRows[0] || null;
      const flowDef = flowDefs[0] || null;

      if (cb) setChatbot(cb);
      if (flowNodes) setNodes(flowNodes);
      if (flowDef) {
        setFlowDefId(flowDef.id);
        setStartNodeId(flowDef.start_node);
      }
    } catch (err: any) {
      toast({ title: 'Failed to load', description: err.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const addNode = async (type: NodeType) => {
    if (!chatbotId) return;
    const position = nodes.length;
    const isFirstNode = position === 0;

    const newNode: Partial<FlowNodeRow> = {
      chatbot_id: chatbotId,
      type,
      field: type === 'question' || type === 'collect' ? `field_${position + 1}` : undefined,
      question: type === 'question' ? 'What is your question?' : type === 'end' ? 'Thanks for chatting!' : undefined,
      input_type: type === 'question' ? 'text' : undefined,
      options: [],
      rules: [],
      next: [],
      position,
    };

    try {
      const rows = await supabasePost<FlowNodeRow[]>('flow_nodes', newNode);
      const created = rows[0];
      setNodes(prev => [...prev, created]);
      setSelectedNode(created);

      // If this is the first node, create the flow definition and set it as start
      if (isFirstNode) {
        const fdRows = await supabasePost<any[]>('flow_definitions', { chatbot_id: chatbotId, name: 'Main Flow', start_node: created.id });
        const flowDef = fdRows[0];
        if (flowDef) {
          setFlowDefId(flowDef.id);
          setStartNodeId(created.id);
        }
      }

      // Link previous node to this one if it has no next
      if (nodes.length > 0) {
        const prevNode = nodes[nodes.length - 1];
        if (!prevNode.next || prevNode.next.length === 0) {
          await supabasePatch(`flow_nodes?id=eq.${prevNode.id}`, { next: [created.id] });
          setNodes(prev => prev.map(n => n.id === prevNode.id ? { ...n, next: [created.id] } : n));
        }
      }
    } catch (err: any) {
      toast({ title: 'Failed to add node', description: err.message, variant: 'destructive' });
    }
  };

  const updateNode = async (nodeId: string, updates: Partial<FlowNodeRow>) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, ...updates } : n));
    if (selectedNode?.id === nodeId) {
      setSelectedNode(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  const saveNode = async (node: FlowNodeRow) => {
    setIsSaving(true);
    try {
      await supabasePatch(`flow_nodes?id=eq.${node.id}`, {
        type: node.type,
        field: node.field,
        question: node.question,
        input_type: node.input_type,
        options: node.options,
        validation: node.validation,
        rules: node.rules,
        next: node.next,
        meta: node.meta,
      });
      toast({ title: 'Saved!', description: 'Node updated successfully' });
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteNode = async (nodeId: string) => {
    if (!confirm('Delete this node?')) return;
    try {
      await supabaseDelete(`flow_nodes?id=eq.${nodeId}`);
      setNodes(prev => prev.filter(n => n.id !== nodeId));
      if (selectedNode?.id === nodeId) setSelectedNode(null);
    } catch (err: any) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    }
  };

  const addRule = () => {
    if (!selectedNode) return;
    const newRule: FlowRule = {
      field: selectedNode.field || '',
      operator: 'eq',
      value: '',
      next_node: nodes[0]?.id || '',
    };
    updateNode(selectedNode.id, { rules: [...(selectedNode.rules || []), newRule] });
  };

  const updateRule = (ruleIndex: number, updates: Partial<FlowRule>) => {
    if (!selectedNode) return;
    const newRules = [...(selectedNode.rules || [])];
    newRules[ruleIndex] = { ...newRules[ruleIndex], ...updates };
    updateNode(selectedNode.id, { rules: newRules });
  };

  const removeRule = (ruleIndex: number) => {
    if (!selectedNode) return;
    const newRules = (selectedNode.rules || []).filter((_, i) => i !== ruleIndex);
    updateNode(selectedNode.id, { rules: newRules });
  };

  // Toggle the chatbot's published state. A published flow is what
  // counts as "Active" in the dashboard and is the intended public-facing
  // version. Draft flows are edit-mode only.
  const togglePublish = async () => {
    if (!chatbot) return;
    const nextPublished = !chatbot.published;
    // Optimistic update — flip the UI instantly, revert on failure.
    setChatbot({ ...chatbot, published: nextPublished });
    try {
      await supabasePatch(`chatbots?id=eq.${chatbot.id}`, { published: nextPublished });
      toast({
        title: nextPublished ? 'Published' : 'Unpublished',
        description: nextPublished
          ? 'Your chatbot is now live and embeddable.'
          : 'Your chatbot is back in draft mode.',
      });
    } catch (err: any) {
      // Revert optimistic update
      setChatbot({ ...chatbot, published: !nextPublished });
      toast({ title: 'Publish failed', description: err.message, variant: 'destructive' });
    }
  };

  const copyEmbedCode = () => {
    const code = `<script src="https://intentdrive.ai/widget.js" data-chatbot-id="${chatbotId}"></script>`;
    navigator.clipboard.writeText(code);
    toast({ title: 'Copied!', description: 'Embed code copied to clipboard' });
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-full p-12">
          <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Header */}
        <div className="border-b border-slate-800 bg-slate-900/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/portal" className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">{chatbot?.name || 'Flow Builder'}</h1>
                <p className="text-xs text-slate-500">{nodes.length} nodes · {chatbot?.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Published status pill */}
              <span
                className={
                  chatbot?.published
                    ? 'inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 text-xs font-medium text-emerald-400'
                    : 'inline-flex items-center gap-1.5 rounded-full bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs font-medium text-slate-400'
                }
              >
                <span className={`h-1.5 w-1.5 rounded-full ${chatbot?.published ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
                {chatbot?.published ? 'Live' : 'Draft'}
              </span>
              <Button onClick={() => setShowPreview(!showPreview)} variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <Play className="h-4 w-4 mr-2" /> {showPreview ? 'Hide' : 'Preview'}
              </Button>
              <Button onClick={() => setShowEmbed(!showEmbed)} variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <Code className="h-4 w-4 mr-2" /> Embed
              </Button>
              {/* Publish / Unpublish primary action */}
              <Button
                onClick={togglePublish}
                size="sm"
                className={
                  chatbot?.published
                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                }
              >
                {chatbot?.published ? 'Unpublish' : 'Publish'}
              </Button>
            </div>
          </div>

          {showEmbed && (
            <div className="mt-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <div className="text-xs text-slate-500 mb-2">Copy this snippet to embed on any website:</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-slate-900 rounded-lg text-xs text-emerald-400 font-mono overflow-x-auto">
                  {`<script src="https://intentdrive.ai/widget.js" data-chatbot-id="${chatbotId}"></script>`}
                </code>
                <Button onClick={copyEmbedCode} size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Main editor area */}
        <div className="grid grid-cols-12 gap-0 min-h-[calc(100vh-100px)]">
          {/* Left panel: Node list */}
          <div className="col-span-3 border-r border-slate-800 bg-slate-900/30 p-4 overflow-y-auto">
            <div className="mb-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Add Node</div>
              <div className="grid grid-cols-2 gap-2">
                {NODE_TYPES.map(t => (
                  <button
                    key={t.value}
                    onClick={() => addNode(t.value)}
                    className={`flex items-center gap-2 p-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-${t.color}-500/50 hover:bg-${t.color}-500/5 transition-all text-xs text-slate-300`}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Flow ({nodes.length})</div>
            <div className="space-y-2">
              {nodes.map((node, i) => {
                const typeInfo = NODE_TYPES.find(t => t.value === node.type);
                const isSelected = selectedNode?.id === node.id;
                const isStart = node.id === startNodeId;
                return (
                  <button
                    key={node.id}
                    onClick={() => setSelectedNode(node)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-emerald-500/10 border-emerald-500/50'
                        : 'bg-slate-800/30 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] text-slate-500">#{i + 1}</span>
                      {typeInfo?.icon}
                      <span className="text-xs font-semibold text-white capitalize">{node.type}</span>
                      {isStart && <span className="ml-auto text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">START</span>}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {node.question || node.field || 'Untitled'}
                    </div>
                  </button>
                );
              })}
              {nodes.length === 0 && (
                <div className="text-center py-8 text-xs text-slate-600">
                  No nodes yet. Add one above to start building.
                </div>
              )}
            </div>
          </div>

          {/* Center panel: Node editor */}
          <div className="col-span-6 p-6 overflow-y-auto">
            {selectedNode ? (
              <div className="max-w-xl space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Edit Node</h2>
                  <div className="flex gap-2">
                    <Button onClick={() => saveNode(selectedNode)} disabled={isSaving} size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
                    </Button>
                    <Button onClick={() => deleteNode(selectedNode.id)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Node type */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Type</label>
                  <div className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-white capitalize">
                    {selectedNode.type}
                  </div>
                </div>

                {/* Field name */}
                {(selectedNode.type === 'question' || selectedNode.type === 'collect') && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Field Name (where the answer is stored)</label>
                    <Input
                      value={selectedNode.field || ''}
                      onChange={(e) => updateNode(selectedNode.id, { field: e.target.value })}
                      placeholder="e.g. email, zip_code, age"
                      className="bg-slate-800/50 border-slate-700 text-white"
                    />
                  </div>
                )}

                {/* Question text */}
                {(selectedNode.type === 'question' || selectedNode.type === 'end') && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Message / Question</label>
                    <textarea
                      value={selectedNode.question || ''}
                      onChange={(e) => updateNode(selectedNode.id, { question: e.target.value })}
                      rows={3}
                      placeholder="What would you like to ask?"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                )}

                {/* Input type */}
                {selectedNode.type === 'question' && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Input Type</label>
                    <select
                      value={selectedNode.input_type || 'text'}
                      onChange={(e) => updateNode(selectedNode.id, { input_type: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm"
                    >
                      <option value="text">Text</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="number">Number</option>
                      <option value="select">Select (options)</option>
                      <option value="boolean">Yes/No</option>
                    </select>
                  </div>
                )}

                {/* Options for select */}
                {selectedNode.input_type === 'select' && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Options (one per line)</label>
                    <textarea
                      value={(selectedNode.options || []).join('\n')}
                      onChange={(e) => updateNode(selectedNode.id, { options: e.target.value.split('\n').filter(Boolean) })}
                      rows={4}
                      placeholder="Option 1&#10;Option 2&#10;Option 3"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm font-mono"
                    />
                  </div>
                )}

                {/* Rules */}
                {(selectedNode.type === 'decision' || selectedNode.type === 'question') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-slate-400 uppercase tracking-wider">Branching Rules</label>
                      <Button onClick={addRule} size="sm" variant="outline" className="h-7 text-xs border-slate-700 text-slate-300">
                        <Plus className="h-3 w-3 mr-1" /> Rule
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(selectedNode.rules || []).map((rule, i) => (
                        <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 space-y-2">
                          <div className="flex items-center gap-1 text-xs">
                            <span className="text-slate-500">IF</span>
                            <Input
                              value={rule.field}
                              onChange={(e) => updateRule(i, { field: e.target.value })}
                              placeholder="field"
                              className="h-7 text-xs bg-slate-900 border-slate-700"
                            />
                            <select
                              value={rule.operator}
                              onChange={(e) => updateRule(i, { operator: e.target.value as RuleOperator })}
                              className="h-7 text-xs px-2 rounded bg-slate-900 border border-slate-700 text-white"
                            >
                              {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                            </select>
                            <Input
                              value={String(rule.value ?? '')}
                              onChange={(e) => updateRule(i, { value: e.target.value })}
                              placeholder="value"
                              className="h-7 text-xs bg-slate-900 border-slate-700"
                            />
                            <button onClick={() => removeRule(i)} className="text-slate-500 hover:text-red-400">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <span className="text-slate-500 pl-8">GO TO</span>
                            <select
                              value={rule.next_node}
                              onChange={(e) => updateRule(i, { next_node: e.target.value })}
                              className="flex-1 h-7 text-xs px-2 rounded bg-slate-900 border border-slate-700 text-white"
                            >
                              <option value="">Select node...</option>
                              {nodes.filter(n => n.id !== selectedNode.id).map(n => (
                                <option key={n.id} value={n.id}>
                                  {n.type}: {(n.question || n.field || 'Untitled').substring(0, 30)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                      {(!selectedNode.rules || selectedNode.rules.length === 0) && (
                        <div className="text-xs text-slate-600 text-center py-3">No rules. Click + Rule to add branching logic.</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Default next */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Default Next (when no rule matches)</label>
                  <select
                    value={selectedNode.next?.[0] || ''}
                    onChange={(e) => updateNode(selectedNode.id, { next: e.target.value ? [e.target.value] : [] })}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm"
                  >
                    <option value="">(Flow ends here)</option>
                    {nodes.filter(n => n.id !== selectedNode.id).map(n => (
                      <option key={n.id} value={n.id}>
                        {n.type}: {(n.question || n.field || 'Untitled').substring(0, 30)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Route destination (for route nodes) */}
                {selectedNode.type === 'route' && (
                  <>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Destination Type</label>
                      <select
                        value={(selectedNode.meta?.route_type as string) || 'webhook'}
                        onChange={(e) => updateNode(selectedNode.id, { meta: { ...selectedNode.meta, route_type: e.target.value } })}
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm"
                      >
                        <option value="webhook">Webhook (generic)</option>
                        <option value="ringba">Ringba Call Trigger</option>
                        <option value="retreaver">Retreaver Call Trigger</option>
                        <option value="leadnexus">LeadNexus360 (Ping/Post)</option>
                        <option value="redirect">Redirect (affiliate link)</option>
                        <option value="call">Click-to-Call (tel:)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Destination URL / Number</label>
                      <Input
                        value={(selectedNode.meta?.url as string) || ''}
                        onChange={(e) => updateNode(selectedNode.id, { meta: { ...selectedNode.meta, url: e.target.value } })}
                        placeholder={
                          selectedNode.meta?.route_type === 'ringba' ? 'https://display.ringba.com/enrich/v1/...'
                          : selectedNode.meta?.route_type === 'retreaver' ? 'https://retreaver.com/a/...'
                          : selectedNode.meta?.route_type === 'call' ? '+18005551234'
                          : 'https://example.com/webhook'
                        }
                        className="bg-slate-800/50 border-slate-700 text-white"
                      />
                    </div>

                    {/* ── Revenue routing fields ── */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-800">
                      <div>
                        <label className="block text-xs text-emerald-400 mb-1.5 uppercase tracking-wider font-semibold">💰 Payout ($)</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={(selectedNode.meta?.payout as number) ?? ''}
                          onChange={(e) => updateNode(selectedNode.id, { meta: { ...selectedNode.meta, payout: e.target.value === '' ? undefined : Number(e.target.value) } })}
                          placeholder="25.00"
                          className="bg-slate-800/50 border-emerald-500/30 text-white"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">Used by router to pick highest-paying offer</p>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Vertical</label>
                        <select
                          value={(selectedNode.meta?.vertical as string) || ''}
                          onChange={(e) => updateNode(selectedNode.id, { meta: { ...selectedNode.meta, vertical: e.target.value || undefined } })}
                          className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm"
                        >
                          <option value="">— Select —</option>
                          <option value="auto_insurance">Auto Insurance</option>
                          <option value="home_insurance">Home Insurance</option>
                          <option value="renters_insurance">Renters Insurance</option>
                          <option value="health_insurance">Health Insurance</option>
                          <option value="life_insurance">Life Insurance</option>
                          <option value="medicare">Medicare</option>
                          <option value="debt_relief">Debt Relief</option>
                          <option value="credit_repair">Credit Repair</option>
                          <option value="personal_loan">Personal Loan</option>
                          <option value="mortgage">Mortgage</option>
                          <option value="solar">Solar</option>
                          <option value="roofing">Roofing</option>
                          <option value="warranty">Auto Warranty</option>
                          <option value="legal">Legal Services</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Pivot on Reject (fallback node)</label>
                      <select
                        value={(selectedNode.meta?.pivot_on_reject as string) || ''}
                        onChange={(e) => updateNode(selectedNode.id, { meta: { ...selectedNode.meta, pivot_on_reject: e.target.value || undefined } })}
                        className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm"
                      >
                        <option value="">— None (ends flow on reject) —</option>
                        {nodes.filter(n => n.id !== selectedNode.id && (n.type === 'question' || n.type === 'route')).map(n => (
                          <option key={n.id} value={n.id}>
                            {n.type}: {(n.question || n.field || 'Untitled').substring(0, 40)}
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-slate-500 mt-1">If the user rejects this offer, pivot them to a different vertical instead of ending the flow.</p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Target className="h-12 w-12 text-slate-700 mb-3" />
                <h3 className="text-lg font-semibold text-slate-400 mb-1">Select a node to edit</h3>
                <p className="text-sm text-slate-600">Or add a new node from the left panel to start building your flow.</p>
              </div>
            )}
          </div>

          {/* Right panel: Live preview */}
          {showPreview && (
            <div className="col-span-3 border-l border-slate-800 bg-slate-900/30 p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Live Preview</div>
              <div className="h-[500px]">
                <FlowChatWidget chatbotId={chatbotId} embedded />
              </div>
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
